import { CallConstants } from '../constants';
import { CallGroup } from './CallGroup';
import { CallUser } from './CallUser';
import { Participant } from './Participant';
import { Recording } from './Recording';

/**
 * Represents a call log.
 */
export class CallLog {
  /**
   * The session ID of the call log.
   */
  private sessionId!: string;
  /**
   * The total audio minutes of the call log.
   */
  private totalAudioMinutes!: number;
  /**
   * The total video minutes of the call log.
   */
  private totalVideoMinutes!: number;
  /**
   * The total duration of the call log.
   */
  private totalDuration!: string;
  /**
   * Whether the call log has a recording.
   */
  private hasRecording!: boolean;
  /**
   * The time the call was initiated at.
   */
  private initiatedAt!: number;
  /**
   * The call category of the call log.
   */
  private callCategory!: string;
  /**
   * @type {CallUser}
   * The initiator of the call log.
   */
  private initiator!: CallUser;
  /**
   * @type {CallUser | CallGroup}
   * The receiver of the call log.
   */
  private receiver!: CallUser | CallGroup;
  /**
   * The receiver type of the call log.
   */
  private receiverType!: string;
  /**
   * The status of the call log.
   */
  private status!: string;
  /**
   * The total duration in minutes of the call log.
   */
  private totalDurationInMinutes!: number;
  /**
   * The total number of participants in the call log.
   */
  private totalParticipants!: number;
  /**
   * The type of the call log.
   */
  private type!: string;
  /**
   * The message ID of the call log.
   */
  private mid!: string;
  /**
   * The time the call ended at.
   */
  private endedAt!: number;
  /**
   * @type {Participant[]}
   * The participants of the call log.
   */
  private participants!: Participant[];
  /**
   * @type {Recording[]}
   * The recordings of the call log.
   */
  private recordings!: Recording[];

  /**
   * Creates a new instance of CallLog.
   * @param data - The data to initialize the call log with.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(data: any) {
    Object.assign(this, data);
  }

  /**
   * Gets the session ID of the call log.
   * @returns The session ID of the call log.
   */
  getSessionID(): string {
    return this.sessionId;
  }

  /**
   * Sets the session ID of the call log.
   * @param value - The session ID to set.
   */
  setSessionID(value: string) {
    this.sessionId = value;
  }

  /**
   * Gets the total audio minutes of the call log.
   * @returns The total audio minutes of the call log.
   */
  getTotalAudioMinutes(): number {
    return this.totalAudioMinutes;
  }

  /**
   * Sets the total audio minutes of the call log.
   * @param value - The total audio minutes to set.
   */
  setTotalAudioMinutes(value: number) {
    this.totalAudioMinutes = value;
  }

  /**
   * Gets the total video minutes of the call log.
   * @returns The total video minutes of the call log.
   */
  getTotalVideoMinutes(): number {
    return this.totalVideoMinutes;
  }

  /**
   * Sets the total video minutes of the call log.
   * @param value - The total video minutes to set.
   */
  setTotalVideoMinutes(value: number) {
    this.totalVideoMinutes = value;
  }

  /**
   * Gets the total duration of the call log.
   * @returns The total duration of the call log.
   */
  getTotalDuration(): string {
    return this.totalDuration;
  }

  /**
   * Sets the total duration of the call log.
   * @param value - The total duration to set.
   */
  setTotalDuration(value: string) {
    this.totalDuration = value;
  }

  /**
   * Gets whether the call log has a recording.
   * @returns Whether the call log has a recording.
   */
  getHasRecording(): boolean {
    return this.hasRecording;
  }

  /**
   * Sets whether the call log has a recording.
   * @param value - Whether the call log has a recording.
   */
  setHasRecording(value: boolean) {
    this.hasRecording = value;
  }

  /**
   * Gets the time the call was initiated at.
   * @returns The time the call was initiated at.
   */
  getInitiatedAt(): number {
    return this.initiatedAt;
  }

  /**
   * Sets the time the call was initiated at.
   * @param value - The time the call was initiated at.
   */
  setInitiatedAt(value: number) {
    this.initiatedAt = value;
  }

  /**
   * Gets the call category of the call log.
   * @returns The call category of the call log.
   */
  getCallCategory(): string {
    return this.callCategory;
  }

  /**
   * Sets the call category of the call log.
   * @param value - The call category to set.
   */
  setCallCategory(value: string) {
    this.callCategory = value;
  }

  /**
   * Gets the initiator of the call log.
   * @returns The initiator of the call log.
   */
  getInitiator(): CallUser {
    return this.initiator;
  }

  /**
   * Sets the initiator of the call log.
   * @param value - The initiator to set.
   */
  setInitiator(value: CallUser) {
    this.initiator = value;
  }

  /**
   * Gets the receiver of the call log.
   * @returns The receiver of the call log.
   */
  getReceiver(): CallUser | CallGroup {
    return this.receiver;
  }

  /**
   * Sets the receiver of the call log.
   * @param value - The receiver to set.
   */
  setReceiver(value: CallUser | CallGroup) {
    this.receiver = value;
  }

  /**
   * Gets the receiver type of the call log.
   * @returns The receiver type of the call log.
   */
  getReceiverType(): string {
    return this.receiverType;
  }

  /**
   * Sets the receiver type of the call log.
   * @param value - The receiver type to set.
   */
  setReceiverType(value: string) {
    this.receiverType = value;
  }

  /**
   * Gets the status of the call log.
   * @returns The status of the call log.
   */
  getStatus(): string {
    return this.status;
  }

  /**
   * Sets the status of the call log.
   * @param value - The status to set.
   */
  setStatus(value: string) {
    this.status = value;
  }

  /**
   * Gets the total duration in minutes of the call log.
   * @returns The total duration in minutes of the call log.
   */
  getTotalDurationInMinutes(): number {
    return this.totalDurationInMinutes;
  }

  /**
   * Sets the total duration in minutes of the call log.
   * @param value - The total duration in minutes to set.
   */
  setTotalDurationInMinutes(value: number) {
    this.totalDurationInMinutes = value;
  }

  /**
   * Gets the total number of participants in the call log.
   * @returns The total number of participants in the call log.
   */
  getTotalParticipants(): number {
    return this.totalParticipants;
  }

  /**
   * Sets the total number of participants in the call log.
   * @param value - The total number of participants to set.
   */
  setTotalParticipants(value: number) {
    this.totalParticipants = value;
  }

  /**
   * Gets the type of the call log.
   * @returns The type of the call log.
   */
  getType(): string {
    return this.type;
  }

  /**
   * Sets the type of the call log.
   * @param value - The type to set.
   */
  setType(value: string) {
    this.type = value;
  }

  /**
   * Gets the message ID of the call log.
   * @returns The message ID of the call log.
   */
  getMid(): string {
    return this.mid;
  }

  /**
   * Sets the message ID of the call log.
   * @param value - The message ID to set.
   */
  setMid(value: string) {
    this.mid = value;
  }

  /**
   * Gets the time the call ended at.
   * @returns The time the call ended at.
   */
  getEndedAt(): number {
    return this.endedAt;
  }

  /**
   * Sets the time the call ended at.
   * @param value - The time the call ended at.
   */
  setEndedAt(value: number) {
    this.endedAt = value;
  }

  /**
   * Gets the participants of the call log.
   * @returns The participants of the call log.
   */
  getParticipants(): Participant[] {
    return this.participants;
  }

  /**
   * Sets the participants of the call log.
   * @param value - The participants to set.
   */
  setParticipants(value: Participant[]) {
    this.participants = value;
  }

  /**
   * Gets the recordings of the call log.
   * @returns The recordings of the call log.
   */
  getRecordings(): Recording[] {
    return this.recordings;
  }

  /**
   * Sets the recordings of the call log.
   * @param value - The recordings to set.
   */
  setRecordings(value: Recording[]) {
    this.recordings = value;
  }

  /**
   * Creates a new instance of CallLog from JSON data.
   * @param data - The JSON data to create the call log from.
   * @returns A new instance of CallLog created from the JSON data.
   */
  static callLogFromJson(data: any): CallLog {
    let callLog = new CallLog(data);
    if (data.participants) {
      callLog.participants = data.participants.map((participant: any) =>
        Participant.getParticipantFromJson(participant),
      );
    }
    if (data.recordings) {
      callLog.recordings = data.recordings.map((recording: any) =>
        Recording.getRecordingFromJson(recording),
      );
    }
    if (data.initiator) {
      if (
        data[CallConstants.CALL_KEYS.CALL_DATA] &&
        data[CallConstants.CALL_KEYS.CALL_DATA][
          CallConstants.CALL_KEYS.CALL_ENTITIES
        ] &&
        data[CallConstants.CALL_KEYS.CALL_DATA][
          CallConstants.CALL_KEYS.CALL_ENTITIES
        ][CallConstants.CALL_KEYS.CALL_INITIATOR] &&
        data[CallConstants.CALL_KEYS.CALL_DATA][
          CallConstants.CALL_KEYS.CALL_ENTITIES
        ][CallConstants.CALL_KEYS.CALL_INITIATOR][
          CallConstants.CALL_KEYS.CALL_ENTITY
        ]
      ) {
        let initiatorObj =
          data[CallConstants.CALL_KEYS.CALL_DATA][
            CallConstants.CALL_KEYS.CALL_ENTITIES
          ][CallConstants.CALL_KEYS.CALL_INITIATOR][
            CallConstants.CALL_KEYS.CALL_ENTITY
          ];
        callLog.initiator = CallUser.getUserFromJson(initiatorObj);
      }
    }
    if (data.receiver) {
      if (
        data[CallConstants.CALL_KEYS.CALL_DATA] &&
        data[CallConstants.CALL_KEYS.CALL_DATA][
          CallConstants.CALL_KEYS.CALL_ENTITIES
        ] &&
        data[CallConstants.CALL_KEYS.CALL_DATA][
          CallConstants.CALL_KEYS.CALL_ENTITIES
        ][CallConstants.CALL_KEYS.CALL_RECEIVER] &&
        data[CallConstants.CALL_KEYS.CALL_DATA][
          CallConstants.CALL_KEYS.CALL_ENTITIES
        ][CallConstants.CALL_KEYS.CALL_RECEIVER][
          CallConstants.CALL_KEYS.CALL_ENTITY
        ]
      ) {
        let receiverObj =
          data[CallConstants.CALL_KEYS.CALL_DATA][
            CallConstants.CALL_KEYS.CALL_ENTITIES
          ][CallConstants.CALL_KEYS.CALL_RECEIVER][
            CallConstants.CALL_KEYS.CALL_ENTITY
          ];
        if (data.receiverType == CallConstants.RECEIVER_TYPE_USER) {
          callLog.receiver = CallUser.getUserFromJson(receiverObj);
        } else {
          callLog.receiver = CallGroup.getGroupFromJson(receiverObj);
        }
      }
    }
    return callLog;
  }
}
